/**
 * onboarding-tour.js
 *
 * Generic, reusable one-time guided tour wrapper around react-joyride.
 * Content lives in src/config/onboarding-steps.js; persistence (which
 * flag gates which tour, and marking it seen) lives in auth-context.js /
 * onboarding-service.js. This component only knows how to run a given
 * step list and report back when it's done.
 *
 * Used by both dashboard-page.js (DASHBOARD_TOUR_STEPS) and
 * coin-detail-page.js (COIN_DETAIL_TOUR_STEPS).
 */

import { useCallback } from 'react';
import { Joyride, STATUS, EVENTS } from 'react-joyride';

/**
 * Returns a Joyride function-target that tries each selector in order and
 * uses the first one found in the DOM. Needed for steps that must resolve
 * on both the desktop table layout and the mobile card layout, since those
 * are genuinely different component trees (only one exists at a time) with
 * different class names for the same concept.
 */
export function firstMatch(...selectors) {
  return () => {
    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el) return el;
    }
    return null;
  };
}

export default function OnboardingTour({ run, steps, onDismiss }) {
  const handleEvent = useCallback((data) => {
    if (
      data.type === EVENTS.TOUR_END &&
      (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED)
    ) {
      onDismiss();
    }
  }, [onDismiss]);

  return (
    <Joyride
      run={run}
      steps={steps}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      options={{
        primaryColor:  '#48D6B4',
        textColor:     '#fff',
        backgroundColor: '#26272b',
        arrowColor:    '#26272b',
        overlayColor:  'rgba(0,0,0,0.6)',
        zIndex:        10000,
        buttons:       ['back', 'close', 'primary', 'skip'],
        // Skip the beacon (pulsing dot requiring a click before the tooltip
        // opens) — appropriate for "discover this feature" callouts, but
        // adds friction to a first-run tour where every step should just
        // appear. Confirmed via manual testing.
        skipBeacon:    true,
      }}
    />
  );
}
